import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import * as kratos from '@ory/kratos-client';

import { getKratosUrl } from '../baseUrls';

/*
Provides Kratos API object and various supporting types and helpers.

Used by Kratos-interacting React components in /kratos, which in-turn
support construction of React components implementing UIs/logic for
specific flows.

If not sure where to start, see Flow, and how a specific flow component
like Login uses it.
*/


const configuration = new kratos.Configuration({
  basePath: getKratosUrl(),
  baseOptions: {
    withCredentials: true
  }
});


export const kratosApi = new kratos.FrontendApi(configuration);

// TODO: add any flow types we might use from Ory SDK:
export type ApiFlow =
  kratos.LoginFlow |
  kratos.RegistrationFlow |
  kratos.VerificationFlow |
  kratos.SettingsFlow |
  kratos.RecoveryFlow;


// TODO: any is ugly
export type SubmitResponse = AxiosResponse<any>


/* Maps node key to node value, see kratos/Flow, kratos/Form
   where node key is of form `${GROUP}-{NAME}`.

   See kratos/FormNode, kratos/Form. */
export type BodyValues = Map<string, any>;


/* session react query cache key, should be invalidated
   on successful flow that mutates session.. TODO:
   try using react query mutate on such submits! */
export const sessionQueryKey = ['kratos', 'whoami'];


/* helper for preparing submit body, besides lookup
   also converts "" -> undefined for unset optionals..
   See note in optionals in Flow.
*/
export function bodyValueExtractor(values: BodyValues)
{
  const unsetValueReact = '';
  const unsetValueKratos = undefined;

  return (key: string) => {
    const value = values.get(key);
    if (value === unsetValueReact) {
      return unsetValueKratos;
    }
    return value;
  }
}


/*
React hook wrapper for sessions/whoami (user info) retrieval.

See kratos-client/api.ts for 'interface Session' details,
but, useful fields in particular include: active, id, identity.
*/
export function useUserInfo() {
  async function fetchSessionInfo(): Promise<kratos.Session | null> {
    try {
      const response = await kratosApi.toSession();
      return response.data;
    }
    catch (error: any) {
      const notLoggedIn = error.response.status === 401;
      if (notLoggedIn) {
        return null;
      }
      throw error;
    }
  }

  const query = useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSessionInfo
  });

  const displayName = renderDisplayName(query);
  const userType: 'unknown' | 'authenticated' | 'guest' = (
    query.status !== 'success'
    ? 'unknown'
    : (query?.data?.id ?? null) !== null
    ? 'authenticated'
    : 'guest'
  );
  const session: kratos.Session | null = query?.data ?? null;

  return { userType, displayName, session, query };
}

type UserInfoQuery = ReturnType<typeof useUserInfo>['query'];

function renderDisplayName(userInfoQuery: UserInfoQuery)
: string {
  if (!userInfoQuery.isSuccess) {
    return 'Guest';
  }
  const session = userInfoQuery.data;
  if (!session?.identity) {
    return 'Guest';
  }
  const traits = session.identity.traits;
  return traits.name || traits.username || traits.email;
}

